import { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import type { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";

const getDaysInRange = (start: Date, end: Date): Date[] => {
  const days: Date[] = [];
  let current = dayjs(start);
  const endDay = dayjs(end);
  while (current.isSameOrBefore(endDay, "day")) {
    days.push(current.toDate());
    current = current.add(1, "day");
  }
  return days;
};

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface UseScheduleCalendarOptions {
  externalDateRange?: DateRange;
  externalViewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

/**
 * Hook for managing schedule calendar state and utilities.
 * @param initialViewMode - Initial view mode (daily, weekly, monthly)
 * @param options - Optional external state for controlled usage
 */
export function useScheduleCalendar(
  initialViewMode: ViewMode = "weekly",
  options?: DateRange | UseScheduleCalendarOptions
) {
  // Support legacy signature (just DateRange) and new options signature
  const isLegacySignature = options && ("from" in options || "to" in options);
  const externalDateRange = isLegacySignature
    ? (options as DateRange)
    : (options as UseScheduleCalendarOptions)?.externalDateRange;
  const externalViewMode = isLegacySignature
    ? undefined
    : (options as UseScheduleCalendarOptions)?.externalViewMode;
  const onViewModeChange = isLegacySignature
    ? undefined
    : (options as UseScheduleCalendarOptions)?.onViewModeChange;

  const [isClient, setIsClient] = useState(false);

  // Internal viewMode state (used when no external viewMode is provided)
  const [internalViewMode, setInternalViewMode] =
    useState<ViewMode>(initialViewMode);

  // Use external viewMode if provided, otherwise use internal state
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = useCallback(
    (mode: ViewMode) => {
      if (onViewModeChange) {
        onViewModeChange(mode);
      } else {
        setInternalViewMode(mode);
      }
    },
    [onViewModeChange]
  );

  // Internal date range state (used when no external dateRange is provided)
  const [internalDateRange, setInternalDateRange] = useState<DateRange>(() => {
    const today = new Date();
    if (initialViewMode === "daily") {
      return { from: today, to: today };
    } else if (initialViewMode === "weekly") {
      const startOfWeek = dayjs(today).startOf("week").add(1, "day").toDate();
      const endOfWeek = dayjs(startOfWeek).add(6, "day").toDate();
      return { from: startOfWeek, to: endOfWeek };
    } else {
      const startOfMonth = dayjs(today).startOf("month").toDate();
      const endOfMonth = dayjs(today).endOf("month").toDate();
      return { from: startOfMonth, to: endOfMonth };
    }
  });

  // Use external dateRange if provided, otherwise use internal state
  const dateRange = externalDateRange ?? internalDateRange;
  const setDateRange = setInternalDateRange;

  // Legacy state for backward compatibility (can be removed later)
  const [currentWeek, setCurrentWeek] = useState<Date>(() =>
    dayjs().startOf("week").add(1, "day").toDate()
  );
  const [currentDay, setCurrentDay] = useState<Date>(() =>
    dayjs().startOf("day").toDate()
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    dayjs().startOf("month").toDate()
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Update legacy state when dateRange changes
  useEffect(() => {
    if (dateRange.from) {
      setCurrentDay(dateRange.from);
      setCurrentWeek(dateRange.from);
      setCurrentMonth(dateRange.from);
    }
  }, [dateRange]);

  const timelineData = useMemo(() => {
    if (!dateRange.from) {
      return {
        days: [],
        startDate: new Date(),
        viewMode: viewMode
      };
    }

    const baseDate = dateRange.from;

    if (viewMode === "daily") {
      return {
        days: [baseDate],
        startDate: baseDate,
        viewMode: "daily" as const
      };
    } else if (viewMode === "weekly") {
      // Use the explicit from→to range when both ends are set; cap at 7 days.
      // Fall back to Mon–Sun snap when only `from` is available.
      if (dateRange.from && dateRange.to) {
        const start = dayjs(dateRange.from).startOf("day");
        const end = dayjs(dateRange.to).startOf("day");
        const spanDays = Math.min(end.diff(start, "day") + 1, 7);
        const days = Array.from({ length: spanDays }, (_, i) =>
          start.add(i, "day").toDate()
        );
        return { days, startDate: start.toDate(), viewMode: "weekly" as const };
      }
      // Snap to Mon–Sun of the week containing baseDate
      const d = dayjs(baseDate);
      const dayOfWeek = d.day(); // 0=Sun, 1=Mon, ..., 6=Sat
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = d.add(diffToMonday, "day").startOf("day").toDate();
      const days = Array.from({ length: 7 }, (_, i) =>
        dayjs(weekStart).add(i, "day").toDate()
      );
      return { days, startDate: weekStart, viewMode: "weekly" as const };
    } else {
      // Always compute all days of the month containing baseDate
      const monthStart = dayjs(baseDate).startOf("month").toDate();
      const monthEnd = dayjs(baseDate).endOf("month").toDate();
      const days = getDaysInRange(monthStart, monthEnd);
      return { days, startDate: monthStart, viewMode: "monthly" as const };
    }
  }, [dateRange.from, viewMode]);

  const timeSlots = useMemo(() => {
    if (viewMode !== "daily") return [];
    const slots = [];
    // Full day: 00:00-23:00
    for (let hour = 0; hour <= 23; hour++) {
      slots.push({
        hour,
        label: `${hour.toString().padStart(2, "0")}:00`,
        time: `${hour.toString().padStart(2, "0")}:00`
      });
    }
    return slots;
  }, [viewMode]);

  const navigatePeriod = useCallback(
    (direction: "prev" | "next") => {
      if (viewMode === "daily") {
        setCurrentDay(
          dayjs(currentDay)
            .add(direction === "prev" ? -1 : 1, "day")
            .toDate()
        );
      } else if (viewMode === "weekly") {
        setCurrentWeek(
          dayjs(currentWeek)
            .add(direction === "prev" ? -1 : 1, "week")
            .toDate()
        );
      } else {
        setCurrentMonth(
          dayjs(currentMonth)
            .add(direction === "prev" ? -1 : 1, "month")
            .toDate()
        );
      }
    },
    [viewMode, currentDay, currentWeek, currentMonth]
  );

  const getPeriodLabel = useCallback(() => {
    if (viewMode === "daily") {
      return dayjs(currentDay).format("dddd, MMMM D, YYYY");
    } else if (viewMode === "weekly") {
      const d = dayjs(currentWeek);
      const diffToMonday = d.day() === 0 ? -6 : 1 - d.day();
      const weekStart = d.add(diffToMonday, "day");
      return `${weekStart.format("MMM D")} - ${weekStart.add(6, "day").format("MMM D, YYYY")}`;
    } else {
      return dayjs(currentMonth).format("MMMM YYYY");
    }
  }, [viewMode, currentDay, currentWeek, currentMonth]);

  const formatTime = useCallback(
    (dateStr: string | null | undefined) => {
      if (!isClient || !dateStr) return "--:--";
      // If already in HH:mm format, return as is
      if (/^\d{2}:\d{2}$/.test(dateStr)) return dateStr;
      // Prefer literal time from ISO-like strings to avoid timezone conversion drift.
      const literalTimeMatch = String(dateStr).match(
        /T(\d{2}:\d{2})(?::\d{2})?/
      );
      if (literalTimeMatch?.[1]) {
        return literalTimeMatch[1];
      }
      // Otherwise parse as full datetime
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
    },
    [isClient]
  );

  return {
    isClient,
    viewMode,
    setViewMode,
    currentDay,
    currentWeek,
    currentMonth,
    dateRange,
    setDateRange,
    timelineData,
    timeSlots,
    navigatePeriod,
    getPeriodLabel,
    formatTime
  };
}
